import useStyles from 'isomorphic-style-loader/useStyles';
import { forwardRef, useMemo } from 'react';
import type { DropdownProps } from 'semantic-ui-react';
import { Dropdown } from 'semantic-ui-react';

import type { AssetType } from '@features/applications/applications.models';
import { useLetterTypeahead } from '@hooks/use-letter-typeahead';

import { assetTypeMetadata } from './asset-type.const';
import s from './asset-type-dropdown.scss';

export interface AssetTypeDropdownProps extends Omit<
  DropdownProps,
  'onChange' | 'options' | 'value'
> {
  /**
   * Callback when the selected asset type changes.
   */
  onChange?: (value: AssetType | '') => void;
  /**
   * The currently selected asset type.
   */
  value?: AssetType | '';
}

/**
 * Dropdown for selecting an asset type. Renders each option with its icon,
 * label, and description using SUI's built-in dropdown item layout. The
 * dropdown menu floats wider than the trigger to accommodate descriptions.
 */
export const AssetTypeDropdown = forwardRef<
  HTMLDivElement,
  AssetTypeDropdownProps
>(({ onChange, value, ...rest }, forwardedRef) => {
  useStyles(s);

  const assetTypeOptions = useMemo(
    () =>
      Object.entries(assetTypeMetadata).map(
        ([assetType, { description, icon: Icon, text }]) => ({
          'aria-label': text,
          className: s.option,
          description,
          image: <Icon />,
          text,
          value: assetType,
        }),
      ),
    [],
  );

  const handleKeyDown = useLetterTypeahead({
    getText: option => String(option.text),
    items: assetTypeOptions,
    onMatch: option => onChange?.(option.value as AssetType | ''),
  });

  return (
    <Dropdown
      className={s.root}
      options={assetTypeOptions}
      placeholder="Select a type"
      selectOnBlur={false}
      floating
      selection
      {...rest}
      ref={forwardedRef}
      onChange={(_, { value: selected }) =>
        onChange?.(selected as AssetType | '')
      }
      onKeyDown={handleKeyDown}
      value={value ?? ''}
    />
  );
});

AssetTypeDropdown.displayName = 'AssetTypeDropdown';
